import { t } from "@lingui/macro";
import {
  RouteProp,
  useLinkTo,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAtom, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { Platform } from "react-native";

import {
  isLocallySignedOutAtom,
  preAuthenticatedPath_atom,
} from "../../features/Authentication/atoms";
import { AuthenticationLayout } from "../../features/Authentication/components/AuthenticationLayout";
import { AuthenticationModal } from "../../features/Authentication/components/AuthenticationModal";
import { ExistingSessionRedirectLoader } from "../../features/Authentication/components/ExistingSessionRedirectLoader";
import { executePostLoginRedirect } from "../../features/Authentication/utils/executePostLoginRedirect";
import { resolvePostLoginRedirect } from "../../features/Authentication/utils/resolvePostLoginRedirect";
import {
  BiometricSignInPromptModal,
  getBiometricUnlockEnabled,
} from "../../features/BiometricAuthentication";
import {
  getStoredSessionCurrentClubSlug,
  sessionQueryVersionAtom,
} from "../../features/Session/atoms";
import { BaseNavigatorParamList } from "../../navigators/BaseNavigator/types";
import { UnauthenticatedNavigatorParamList } from "../../navigators/UnauthenticatedStackNavigator/types";
import { useFlags } from "../../utils/launchDarkly";

type InitialScreenProps = {
  defaultClubSlug?: null | string;
  hasSession?: boolean;
};

export const InitialScreen = ({
  defaultClubSlug = null,
  hasSession = false,
}: InitialScreenProps) => {
  const { biometricAuth } = useFlags();
  const [preAuthenticatedPath, setPreAuthenticatedPath] = useAtom(
    preAuthenticatedPath_atom
  );
  const [hasCompletedLogin, setHasCompletedLogin] = useState(false);
  const [hasBiometricSession, setHasBiometricSession] = useState(false);
  const [lastSelectedClubSlug, setLastSelectedClubSlug] = useState<
    string | null
  >(null);
  const hasHandledExistingSession = useRef(false);
  const incrementSessionQueryVersion = useSetAtom(sessionQueryVersionAtom);
  const [isLocallySignedOut, setIsLocallySignedOut] = useAtom(
    isLocallySignedOutAtom
  );

  const appNavigation =
    useNavigation<NativeStackNavigationProp<BaseNavigatorParamList>>();
  const linkTo = useLinkTo();

  const route =
    useRoute<RouteProp<UnauthenticatedNavigatorParamList, "Initial">>();

  const routeAfterLoginParam = route.params?.routeAfterLogin;

  useEffect(() => {
    if (Platform.OS === "web") {
      return;
    }

    let isMounted = true;

    const loadLastSelectedClubSlug = async () => {
      try {
        const slug = await getStoredSessionCurrentClubSlug();

        if (isMounted && slug) {
          setLastSelectedClubSlug(slug);
        }
      } catch {
        // The preferred club is optional; biometric sign-in can use the first active club.
      }
    };

    void loadLastSelectedClubSlug();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      !hasCompletedLogin &&
      !hasSession &&
      routeAfterLoginParam &&
      routeAfterLoginParam !== preAuthenticatedPath
    ) {
      setPreAuthenticatedPath(routeAfterLoginParam);
    }
  }, [
    hasCompletedLogin,
    hasSession,
    preAuthenticatedPath,
    routeAfterLoginParam,
    setPreAuthenticatedPath,
  ]);

  useEffect(() => {
    let isMounted = true;

    const loadBiometricSession = async () => {
      if (biometricAuth !== true || hasSession) {
        setHasBiometricSession(false);
        return;
      }

      const isEnabled = await getBiometricUnlockEnabled();

      if (isMounted && isEnabled) {
        setHasBiometricSession(true);
      }
    };

    void loadBiometricSession();

    return () => {
      isMounted = false;
    };
  }, [biometricAuth, hasSession]);

  useEffect(() => {
    if (!hasSession || hasHandledExistingSession.current) {
      return;
    }

    hasHandledExistingSession.current = true;

    const redirectPath = resolvePostLoginRedirect({
      locationSearch:
        Platform.OS === "web" ? window.location.search : undefined,
      preAuthenticatedPath,
      routeAfterLoginParam,
    });

    const didRedirect = executePostLoginRedirect({
      clearPreAuthenticatedPath: () => setPreAuthenticatedPath(null),
      redirectExternal: (url) => {
        if (Platform.OS === "web") {
          window.location.href = url;
          return;
        }

        appNavigation.navigate("Club", {
          clubSlug: defaultClubSlug ?? "",
          screen: "Home",
          params: {
            screen: "WebView",
            params: {
              title: t`Continue`,
              url,
            },
          },
        });
      },
      redirectInApp: (path) => {
        linkTo(path);
      },
      redirectPath,
    });

    if (!didRedirect && defaultClubSlug) {
      if (Platform.OS === "web") {
        linkTo(`/patron/club/${defaultClubSlug}`);
        return;
      }

      appNavigation.navigate("Club", {
        clubSlug: defaultClubSlug,
        screen: "Home",
        params: { screen: "MainHome" },
      });
    }
  }, [
    appNavigation,
    defaultClubSlug,
    hasSession,
    linkTo,
    preAuthenticatedPath,
    routeAfterLoginParam,
    setPreAuthenticatedPath,
  ]);

  const onComplete = (clubSlug?: string) => {
    setHasCompletedLogin(true);
    setIsLocallySignedOut(false);
    incrementSessionQueryVersion((current) => current + 1);

    const redirectPath = resolvePostLoginRedirect({
      locationSearch:
        Platform.OS === "web" ? window.location.search : undefined,
      preAuthenticatedPath,
      routeAfterLoginParam,
    });

    const didRedirect = executePostLoginRedirect({
      clearPreAuthenticatedPath: () => setPreAuthenticatedPath(null),
      redirectExternal: (url) => {
        if (Platform.OS === "web") {
          window.location.href = url;
          return;
        }

        appNavigation.navigate("Club", {
          clubSlug: clubSlug ?? "",
          screen: "Home",
          params: {
            screen: "WebView",
            params: {
              title: t`Continue`,
              url,
            },
          },
        });
      },
      redirectInApp: (path) => {
        linkTo(path);
      },
      redirectPath,
    });

    if (!didRedirect) {
      appNavigation.navigate("Club", {
        clubSlug: clubSlug ?? "",
        screen: "Home",
        params: { screen: "MainHome" },
      });
    }
  };

  if (hasSession) {
    return <ExistingSessionRedirectLoader />;
  }

  return (
    <>
      <AuthenticationLayout>
        <AuthenticationModal
          isOpen={true}
          onComplete={onComplete}
          preferredBiometricClubSlug={lastSelectedClubSlug}
          hideCloseButton
        />
      </AuthenticationLayout>
      <BiometricSignInPromptModal
        isOpen={
          hasBiometricSession && !hasCompletedLogin && !isLocallySignedOut
        }
        onComplete={onComplete}
        onDismiss={() => setHasBiometricSession(false)}
        preferredClubSlug={lastSelectedClubSlug}
      />
    </>
  );
};
